import React, { useState, useEffect } from 'react';
import { formatDate, parseLocalDate } from '../../utils/format';
import './PicklistView.css';
import '../../styles/DataGrid.css';
import Card from '../common/Card';
import Table from '../common/Table';
import Pagination from '../common/Pagination';
import { picklistApi, PicklistItem, PicklistStats } from '../../services/picklistApi';

interface PicklistViewProps {
  initialFilter?: 'all' | 'due' | 'missing' | 'overdue';
  onNavigateToUpload?: () => void;
}

const PicklistView: React.FC<PicklistViewProps> = ({ initialFilter = 'due', onNavigateToUpload }) => {
  const [activeTab, setActiveTab] = useState<'due' | 'overdue' | 'missing'>(
    initialFilter === 'all' ? 'due' : initialFilter as 'due' | 'overdue' | 'missing'
  );
  const [picklistItems, setPicklistItems] = useState<PicklistItem[]>([]);
  const [picklistStats, setPicklistStats] = useState<PicklistStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // 'all' means no client filter. Otherwise holds the exact client_name to filter on.
  const [selectedClient, setSelectedClient] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsData, statsData] = await Promise.all([
        picklistApi.getItems(),
        picklistApi.getStats()
      ]);
      setPicklistItems(itemsData);
      setPicklistStats(statsData);
      setError(null);
    } catch (err) {
      console.error('Failed to load picklist data:', err);
      setError('Failed to load picklist data. Please check your connection and try again.');
      setPicklistItems([]);
      setPicklistStats(null);
    } finally {
      setLoading(false);
    }
  };


  // Distinct list of clients that appear in the currently-loaded picklist items.
  // Derived from picklistItems so the dropdown only ever shows clients with real data
  // — no extra API call needed.
  const clientOptions = Array.from(
    new Set(picklistItems.map(i => i.clientName).filter((n): n is string => !!n))
  ).sort((a, b) => a.localeCompare(b));

  // True when the selectedClient filter is active. Centralises the "is filter on?" check
  // so counts/data/CSV all use the same predicate.
  const isClientFiltered = selectedClient !== 'all';
  const matchesClient = (item: PicklistItem) =>
    !isClientFiltered || item.clientName === selectedClient;

  // Calculate counts for stat cards (independent of active tab, but reflects the
  // selected client filter so tab badges match the rows actually shown).
  const calculateCounts = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    const due = picklistItems.filter(item => {
      if (!matchesClient(item)) return false;
      const expectedDate = parseLocalDate(item.nextExpectedDate);
      if (!expectedDate) return false;
      expectedDate.setHours(0, 0, 0, 0);
      return item.status === 'pending' && expectedDate >= today && expectedDate <= weekFromNow;
    });

    const overdue = picklistItems.filter(item => {
      if (!matchesClient(item)) return false;
      if (item.status === 'overdue') return true;
      const expectedDate = parseLocalDate(item.nextExpectedDate);
      if (!expectedDate) return false;
      expectedDate.setHours(0, 0, 0, 0);
      return item.status === 'pending' && expectedDate < today;
    });

    const missing = picklistItems.filter(item => matchesClient(item) && item.status === 'missing');
    const collected = picklistItems.filter(item => matchesClient(item) && item.status === 'collected');

    return {
      dueCount: due.length,
      overdueCount: overdue.length,
      missingCount: missing.length,
      collectedCount: collected.length
    };
  };
  
  const counts = calculateCounts();
  
  // Get data for the active tab only (also applies the selectedClient filter).
  const getActiveData = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    return picklistItems.filter(item => {
      if (!matchesClient(item)) return false;
      switch (activeTab) {
        case 'due': {
          const dueDateForDue = parseLocalDate(item.nextExpectedDate);
          if (!dueDateForDue) return false;
          dueDateForDue.setHours(0, 0, 0, 0);
          return item.status === 'pending' && dueDateForDue >= today && dueDateForDue <= weekFromNow;
        }
        case 'overdue': {
          if (item.status === 'overdue') return true;
          const dueDateForOverdue = parseLocalDate(item.nextExpectedDate);
          if (!dueDateForOverdue) return false;
          dueDateForOverdue.setHours(0, 0, 0, 0);
          return item.status === 'pending' && dueDateForOverdue < today;
        }
        case 'missing':
          return item.status === 'missing';
        default:
          return false;
      }
    });
  };

  // Reset to page 1 when tab or client filter changes — otherwise a user filtering down
  // could end up on a page number that no longer exists.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedClient]);

  // Reset to page 1 when rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  // Pagination logic
  const activeData = getActiveData();
  const totalPages = Math.ceil(activeData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = activeData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
  };


  const getUtilityIcon = (type: string) => {
    const icons = {
      'Electric': '⚡',
      'Gas': '🔥',
      'Water': '💧',
      'CWS': '❄️'
    };
    return icons[type as keyof typeof icons] || '📄';
  };

  const columns = [
    { key: 'customer',      title: 'Customer',        width: '17%' },
    { key: 'property',      title: 'Property',        width: '17%' },
    { key: 'contact',       title: 'Contact Person',  width: '17%' },
    { key: 'provider',      title: 'Utility Provider', width: '15%' },
    { key: 'accountNumber', title: 'Account #',       width: '10%' },
    { key: 'frequency',     title: 'Billing',         width: '8%', align: 'center' as const },
    { key: 'lastInvoice',   title: 'Last Invoice',    width: '8%', align: 'center' as const },
    { key: 'dueDate',       title: 'Invoice Due',     width: '8%', align: 'center' as const }
  ];

  const transformedData = paginatedData.map(item => ({
    customer: (
      <div className="customer-info">
        <div className="customer-name">{item.clientName || '-'}</div>
        {item.clientAddress && (
          <div className="address-text" title={item.clientAddress}>
            {item.clientAddress.length > 35 ?
              item.clientAddress.substring(0, 35) + '...' :
              item.clientAddress}
          </div>
        )}
      </div>
    ),
    property: (
      <div className="property-info">
        {item.isSupplier ? (
          // Supplier-contract picklist items aren't tied to a single property — one
          // supplier invoice covers many sites. Show a friendly label instead of "-"
          // so the row reads clearly.
          <>
            <div className="property-name supplier-row-label">Supplier Contract</div>
          </>
        ) : (
          <>
            <div className="property-name">{item.propertyName || item.propertyAddress?.split(',')[0] || '-'}</div>
            {item.propertyAddress && (
              <div className="address-text" title={item.propertyAddress}>
                {item.propertyAddress.length > 35 ?
                  item.propertyAddress.substring(0, 35) + '...' :
                  item.propertyAddress}
              </div>
            )}
          </>
        )}
      </div>
    ),
    contact: (
      <div className="contact-info">
        <div className="contact-name">{item.primaryContact || '-'}</div>
        {item.contactEmail && (
          <div className="contact-detail">
            <a href={`mailto:${item.contactEmail}`} title={item.contactEmail}>
              {item.contactEmail.length > 28 ? 
                item.contactEmail.substring(0, 28) + '...' : 
                item.contactEmail}
            </a>
          </div>
        )}
        {item.contactPhone && <div className="contact-detail">{item.contactPhone}</div>}
      </div>
    ),
    provider: (
      <div className="provider-cell">
        <span className="utility-icon">{getUtilityIcon(item.utilityType)}</span>
        <span className="provider-name">{item.providerName || '-'}</span>
      </div>
    ),
    accountNumber: <span className="account-number">{item.accountNumber || '-'}</span>,
    frequency: (
      <span className="billing-frequency">
        {item.billingFrequency ? 
          item.billingFrequency.charAt(0).toUpperCase() + item.billingFrequency.slice(1) : 
          'Monthly'}
      </span>
    ),
    lastInvoice: item.lastInvoiceDate && item.lastInvoiceDate !== 'Invalid Date' ? (
      <span className="last-invoice-date">
        {formatDate(item.lastInvoiceDate, {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })}
      </span>
    ) : <span className="last-invoice-date">-</span>,
    dueDate: item.nextExpectedDate && item.nextExpectedDate !== 'Invalid Date' ? (
      <span className="expected-date">
        {formatDate(item.nextExpectedDate, {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })}
      </span>
    ) : <span className="expected-date">-</span>
  }));


  return (
    <div className="picklist-view">
      <Card className="data-grid-table">
        <div className="data-grid-navigation">
          <div className="data-grid-tabs">
            <button 
              className={`data-grid-tab ${activeTab === 'due' ? 'active' : ''}`}
              onClick={() => setActiveTab('due')}
            >
              Due <span className="count">{counts.dueCount}</span>
            </button>
            <button 
              className={`data-grid-tab ${activeTab === 'overdue' ? 'active' : ''}`}
              onClick={() => setActiveTab('overdue')}
            >
              Overdue <span className="count">{counts.overdueCount}</span>
            </button>
            <button 
              className={`data-grid-tab ${activeTab === 'missing' ? 'active' : ''}`}
              onClick={() => setActiveTab('missing')}
            >
              Missing <span className="count">{counts.missingCount}</span>
            </button>
          </div>
          <div className="data-grid-actions">
            <div className="data-grid-client-filter">
              <label htmlFor="picklist-client-filter">Client</label>
              <select
                id="picklist-client-filter"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                disabled={loading}
                aria-label="Filter picklist by client"
              >
                <option value="all">All clients</option>
                {clientOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <button
              className="action-icon-btn export"
              onClick={() => picklistApi.exportCsv(undefined, isClientFiltered ? selectedClient : undefined)}
              title={isClientFiltered
                ? `Export picklist items for ${selectedClient} to CSV`
                : 'Export all picklist items to CSV'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </button>
            <div className="data-grid-rows-control">
              <span>Show</span>
              <select
                value={rowsPerPage}
                onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
              >
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="data-grid-body">
          {activeData.length > 0 ? (
            <>
              <Table
                columns={columns}
                data={transformedData}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                totalItems={activeData.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </>
          ) : (
            <div className="data-grid-empty">
              <p>No {activeTab} invoices found.</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PicklistView;
